import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api/axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaTruck, FaFileCsv, FaSearch, FaUser, FaCheckCircle, FaExclamationCircle, FaSyncAlt } from 'react-icons/fa';
import { translateMedicineName } from '../../utils/medTranslations';

interface Reservation {
    id: number;
    user_email: string | null;
    user_name?: string | null;
    service: string;
    visit_type?: string | null;
    date: string;
    time: string;
    status: string;
    payment_status?: string | null;
    total_amount_jpy?: number | null;
    shipping_status?: string | null;
    tracking_number?: string | null;
    shipped_at?: string | null;
    shipping_carrier?: string | null;
    selected_price_plan_ids?: number[] | null;
    medicine_ids?: number[] | null;
    refill_max_months: number;
    refill_used_months: number;
    refill_max_quantity: number;
    refill_used_quantity: number;
    is_refill: boolean;
    parent_reservation_id?: number | null;
    prescription_text?: string;
    user_profile?: {
        full_name?: string | null;
        furigana?: string | null;
        gender?: string | null;
        birth_date?: string | null;
        phone_number?: string | null;
        postal_code?: string | null;
        address?: string | null;
        karte_number?: string | null;
        chart_number?: string | null;
        medical_record_number?: string | null;
    };
}

interface Medicine {
    id: number;
    name: string;
    service_type?: string;
    price_plans: {
        id: number;
        plan_type: string;
        duration_months: number;
        quantity_units?: number | null;
    }[];
}

const AdminDeliveryManagement: React.FC = () => {
    const [reservations, setReservations] = useState<Reservation[]>([]);
    const [medicines, setMedicines] = useState<Medicine[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [shippingFilter, setShippingFilter] = useState<'all' | 'pending' | 'shipped'>('all');

    // CSV & Tracking related states
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [showCsvModal, setShowCsvModal] = useState(false);
    const [csvCoolType, setCsvCoolType] = useState<number>(0);
    const [csvShipDate, setCsvShipDate] = useState<string>('');
    const [senderAddresses, setSenderAddresses] = useState<any[]>([]);
    const [selectedSenderId, setSelectedSenderId] = useState<number | undefined>(undefined);

    const [showTrackingModal, setShowTrackingModal] = useState(false);
    const [trackingTarget, setTrackingTarget] = useState<Reservation | null>(null);
    const [trackingInput, setTrackingInput] = useState('');
    const [trackingCarrier, setTrackingCarrier] = useState('yamato');

    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [resReservations, resMedicines] = await Promise.all([
                api.get('/admin/reservations', {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                }),
                api.get('/admin/medicines', { params: { limit: 1000 } })
            ]);
            // Filter only completed, paid (Include Refills as well)
            const filtered = (resReservations.data as Reservation[]).filter(
                (r) => r.status === 'completed' && r.payment_status === 'paid' && !r.is_refill
            );
            setReservations(filtered);
            setMedicines(resMedicines.data);
        } catch (err) {
            console.error('Error fetching delivery data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const role = localStorage.getItem('role');
        if (role !== 'admin') {
            navigate('/');
            return;
        }
        fetchInitialData();
    }, [navigate]);

    // Lookup table for medicines/plans
    const planMap = useMemo(() => {
        const map = new Map<number, { medName: string, quantity: number, serviceType: string }>();
        medicines.forEach(m => {
            m.price_plans.forEach(p => {
                map.set(p.id, {
                    medName: m.name,
                    quantity: p.quantity_units || 0,
                    serviceType: m.service_type || ''
                });
            });
        });
        return map;
    }, [medicines]);

    const getMedicineDisplay = (r: Reservation) => {
        const plans = (r as any).selected_price_plan_ids || [];
        const planMonths = (r as any).selected_plan_months || (r as any).selected_price_plan_months || {};

        if (plans.length > 0) {
            const names = plans.map((pid: number) => {
                const info = planMap.get(pid);
                if (!info || info.serviceType === 'shipping') return null;

                const unitBase = info.quantity || 1;
                const months = planMonths[pid] || 1;
                const totalQuantity = unitBase * months;
                const translatedName = translateMedicineName(info.medName, i18n.language);

                return `${translatedName} (${totalQuantity}${t('admin.quantity_unit', '个')})`;
            }).filter(Boolean);

            if (names.length > 0) return names.join(', ');
        }

        // Fallback: Use medicine_ids for old reservations
        const medIds = (r as any).medicine_ids || [];
        if (medIds.length > 0) {
            const medNames = medIds.map((mid: number) => {
                const med = medicines.find(m => m.id === mid);
                if (med && med.service_type !== 'shipping') {
                    const translatedName = translateMedicineName(med.name, i18n.language);
                    return `${translatedName} (1${t('admin.quantity_unit', '个')})`;
                }
                return null;
            }).filter(Boolean);
            if (medNames.length > 0) return medNames.join(', ');
        }

        return '-';
    };

    const getShippingDisplay = (r: Reservation) => {
        const plans = (r as any).selected_price_plan_ids || [];
        return plans.map((pid: number) => {
            const info = planMap.get(pid);
            if (info && info.serviceType === 'shipping') {
                return translateMedicineName(info.medName, i18n.language);
            }
            return null;
        }).filter(Boolean).join(', ') || '-';
    };

    const filteredList = useMemo(() => {
        return reservations.filter((r) => {
            const matchesSearch =
                !searchTerm ||
                (r.user_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (r.user_email || '').toLowerCase().includes(searchTerm.toLowerCase());

            const matchesShipping =
                shippingFilter === 'all' ||
                (shippingFilter === 'pending' && r.shipping_status !== 'shipped') ||
                (shippingFilter === 'shipped' && r.shipping_status === 'shipped');

            return matchesSearch && matchesShipping;
        });
    }, [reservations, searchTerm, shippingFilter]);

    const toggleSelect = (id: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === filteredList.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredList.map((r) => r.id)));
        }
    };

    const openCsvModal = async () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setCsvShipDate(tomorrow.toISOString().slice(0, 10));

        try {
            const resp = await api.get('/admin/sender-addresses');
            setSenderAddresses(resp.data);
            if (resp.data.length > 0 && selectedSenderId === undefined) {
                setSelectedSenderId(resp.data[0].id);
            }
        } catch (err) {
            console.error('Failed to fetch sender addresses', err);
        }
        setShowCsvModal(true);
    };

    const handleGenerateCsv = async () => {
        const eligibleIds = Array.from(selectedIds);
        if (eligibleIds.length === 0) return;

        try {
            const res = await api.post(
                '/admin/reservations/csv/yamato',
                {
                    reservation_ids: eligibleIds,
                    cool_type: csvCoolType,
                    sender_address_id: selectedSenderId,
                    ship_date: csvShipDate,
                },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                    responseType: 'blob',
                }
            );

            const dateStr = new Date().toISOString().slice(0, 10);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `yamato_export_${dateStr}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            setShowCsvModal(false);
            setSelectedIds(new Set());
        } catch (e) {
            console.error(e);
            alert(t('admin.csv_export_failed'));
        }
    };

    const openTrackingModal = (r: Reservation) => {
        setTrackingTarget(r);
        setTrackingInput(r.tracking_number || '');
        setTrackingCarrier(r.shipping_carrier === 'yu_pack' ? 'yupack' : (r.shipping_carrier || 'yamato'));
        setShowTrackingModal(true);
    };

    const saveTrackingNumber = async () => {
        if (!trackingTarget) return;
        try {
            await api.patch(
                `/admin/reservations/${trackingTarget.id}/tracking`,
                { 
                    tracking_number: trackingInput,
                    shipping_carrier: trackingCarrier
                },
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
            );
            await fetchInitialData();
            setShowTrackingModal(false);
            alert(t('admin.shipping_update_success') || '追跡番号を登録しました');
        } catch (e) {
            console.error(e);
            alert(t('admin.shipping_update_failed') || '登録に失敗しました');
        }
    };

    const getKarteNumber = (r: Reservation): string | null => {
        const p = r.user_profile || {};
        const candidates = [
            (p as any).karte_number,
            (p as any).chart_number,
            (p as any).medical_record_number,
        ];
        return candidates.find((v) => typeof v === 'string' && v.trim().length > 0) || null;
    };

    return (
        <div className="p-4 sm:p-6 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <FaTruck className="text-coral" />
                        {t('admin.delivery_management') || '配送管理'}
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        {t('admin.delivery_desc_page') || '決済完了済みユーザーの配送状況を管理します'}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={openCsvModal}
                        disabled={selectedIds.size === 0}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition shadow-sm"
                    >
                        <FaFileCsv />
                        {t('admin.generate_csv') || 'CSV出力'}
                        {selectedIds.size > 0 && ` (${selectedIds.size})`}
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border mb-6 overflow-hidden">
                <div className="p-4 border-b bg-gray-50 flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-1">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder={t('admin.search_user_ph') || '名前またはメールで検索'}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-300 outline-none"
                        />
                    </div>
                    <div className="flex gap-2">
                        {(['all', 'pending', 'shipped'] as const).map((mode) => (
                            <button
                                key={mode}
                                onClick={() => setShippingFilter(mode)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${shippingFilter === mode
                                    ? 'bg-coral text-white shadow-md'
                                    : 'bg-white text-gray-600 border hover:bg-gray-50'
                                    }`}
                            >
                                {mode === 'all' ? t('common.all') || 'すべて' : mode === 'pending' ? t('admin.shipping_pending') || '未発送' : t('admin.shipped') || '発送済'}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {/* ▼ モバイル: カード表示（横スクロールバーを出さずに縦積み） */}
                    <div className="md:hidden divide-y">
                        {loading ? (
                            // ローディング状態（テーブルと同じ文言）
                            <div className="px-4 py-8 text-center text-gray-500">
                                {t('common.loading') || '読み込み中...'}
                            </div>
                        ) : filteredList.length === 0 ? (
                            // 空状態（テーブルと同じ文言）
                            <div className="px-4 py-8 text-center text-gray-500">
                                {t('common.no_data') || 'データがありません'}
                            </div>
                        ) : (
                            filteredList.map((r) => {
                                const karte = getKarteNumber(r);
                                const isShipped = r.shipping_status === 'shipped';
                                return (
                                    <div key={r.id} className="p-4">
                                        {/* ヘッダー: チェックボックス + 患者 + 配送ステータス */}
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex items-start gap-3 min-w-0">
                                                {/* 選択チェックボックス（CSV出力対象・テーブルと同じ状態/ハンドラ） */}
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.has(r.id)}
                                                    onChange={() => toggleSelect(r.id)}
                                                    className="w-4 h-4 rounded text-coral mt-1 shrink-0"
                                                />
                                                <div className="w-8 h-8 rounded-full bg-coral-light flex items-center justify-center text-coral shrink-0">
                                                    <FaUser size={14} />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="text-[10px] font-mono text-gray-400">ID: {r.id}</div>
                                                    <div className="font-bold flex items-center gap-1 flex-wrap break-words min-w-0">
                                                        {r.user_name || '-'}
                                                        {karte && <span className="text-[10px] bg-gray-200 px-1 rounded font-normal break-all">#{karte}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                            {/* 配送ステータスのバッジ */}
                                            {isShipped ? (
                                                <div className="shrink-0 flex items-center gap-1.5 text-emerald-600 font-bold bg-emerald-50 px-2 py-1 rounded inline-flex border border-emerald-100 text-xs">
                                                    <FaCheckCircle size={12} />
                                                    {t('admin.shipped') || '発送済'}
                                                </div>
                                            ) : (
                                                <div className="shrink-0 flex items-center gap-1.5 text-orange-600 font-bold bg-orange-50 px-2 py-1 rounded inline-flex border border-orange-100 text-xs">
                                                    <FaExclamationCircle size={12} />
                                                    {t('admin.shipping_pending') || '未発送'}
                                                </div>
                                            )}
                                        </div>

                                        {/* 明細フィールド（ラベル：値） */}
                                        <div className="mt-3 space-y-1.5 text-xs">
                                            {/* 处方药品 */}
                                            <div className="flex gap-1 min-w-0">
                                                <span className="text-gray-400 shrink-0">{t('admin.medicine_name', '处方药品')}：</span>
                                                <span className="text-gray-700 min-w-0 break-words whitespace-pre-wrap">{getMedicineDisplay(r)}</span>
                                            </div>
                                            {/* 配送项目 */}
                                            <div className="flex gap-1 min-w-0">
                                                <span className="text-gray-400 shrink-0">{t('admin.shipping_fee', '配送项目')}：</span>
                                                <span className="text-gray-500 min-w-0 break-words whitespace-pre-wrap">{getShippingDisplay(r)}</span>
                                            </div>
                                            {/* 服务 */}
                                            <div className="flex items-center gap-1 flex-wrap">
                                                <span className="text-gray-400 shrink-0">{t('admin.service', '服务')}：</span>
                                                <span className="px-2 py-0.5 bg-gray-100 rounded text-[11px] font-semibold text-gray-600 border">
                                                    {t(`service.${r.service}`, r.service)}
                                                </span>
                                            </div>
                                            {/* 日時 */}
                                            <div className="flex gap-1 flex-wrap">
                                                <span className="text-gray-400 shrink-0">{t('doctor.date') || '日時'}：</span>
                                                <span className="text-gray-700 break-words">{r.date} <span className="text-gray-400">{r.time}</span></span>
                                            </div>
                                            {/* 追跡番号（登録済みの場合のみ） */}
                                            {r.tracking_number && (
                                                <div className="flex gap-1 min-w-0">
                                                    <span className="text-gray-400 shrink-0">{t('admin.shipping_tracking_number') || '追跡番号'}：</span>
                                                    <span className="text-gray-500 min-w-0 break-all font-mono">{r.tracking_number}</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* 操作（追跡番号入力モーダルを開く・テーブルと同じハンドラ） */}
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            <button
                                                onClick={() => openTrackingModal(r)}
                                                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition shadow-sm ${isShipped
                                                    ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border border-emerald-300'
                                                    : 'bg-coral text-white hover:opacity-90'
                                                    }`}
                                            >
                                                {isShipped ? t('admin.shipped') || '発送済' : t('admin.shipping_input_tracking') || '配送入力'}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* ▼ デスクトップ: テーブル表示 */}
                    <table className="w-full text-sm text-left hidden md:table">
                        <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                            <tr>
                                <th className="px-4 py-3 w-10">
                                    <input
                                        type="checkbox"
                                        checked={filteredList.length > 0 && selectedIds.size === filteredList.length}
                                        onChange={toggleSelectAll}
                                        className="w-4 h-4 rounded text-coral"
                                    />
                                </th>
                                <th className="px-4 py-3">{t('doctor.patient') || '患者'}</th>
                                <th className="px-4 py-3">{t('admin.medicine_name', '处方药品')}</th>
                                <th className="px-4 py-3">{t('admin.shipping_fee', '配送项目')}</th>
                                <th className="px-4 py-3">{t('admin.service', '服务')}</th>
                                <th className="px-4 py-3">{t('doctor.date') || '日時'}</th>
                                <th className="px-4 py-3">{t('admin.shipping_status') || '配送ステータス'}</th>
                                <th className="px-4 py-3 text-right">{t('admin.actions') || '操作'}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                                        {t('common.loading') || '読み込み中...'}
                                    </td>
                                </tr>
                            ) : filteredList.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                                        {t('common.no_data') || 'データがありません'}
                                    </td>
                                </tr>
                            ) : (
                                filteredList.map((r) => {
                                    const karte = getKarteNumber(r);
                                    const isShipped = r.shipping_status === 'shipped';
                                    return (
                                        <tr key={r.id} className="hover:bg-gray-50 transition">
                                            <td className="px-4 py-3">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.has(r.id)}
                                                    onChange={() => toggleSelect(r.id)}
                                                    className="w-4 h-4 rounded text-coral"
                                                />
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-coral-light flex items-center justify-center text-coral">
                                                        <FaUser size={14} />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="text-[10px] font-mono text-gray-400">ID: {r.id}</div>
                                                        <div className="font-bold flex items-center gap-1">
                                                            {r.user_name || '-'}
                                                            {karte && <span className="text-[10px] bg-gray-200 px-1 rounded font-normal">#{karte}</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="max-w-[200px] whitespace-pre-wrap leading-snug text-[11px]">
                                                    {getMedicineDisplay(r)}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="max-w-[150px] whitespace-pre-wrap leading-snug text-[11px] text-gray-500">
                                                    {getShippingDisplay(r)}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className="px-2 py-1 bg-gray-100 rounded text-[11px] font-semibold text-gray-600 border">
                                                    {t(`service.${r.service}`, r.service)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="text-gray-700">{r.date}</div>
                                                <div className="text-gray-400 text-xs">{r.time}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                {isShipped ? (
                                                    <div className="flex items-center gap-1.5 text-emerald-600 font-bold bg-emerald-50 px-2 py-1 rounded inline-flex border border-emerald-100">
                                                        <FaCheckCircle size={12} />
                                                        {t('admin.shipped') || '発送済'}
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-1.5 text-orange-600 font-bold bg-orange-50 px-2 py-1 rounded inline-flex border border-orange-100">
                                                        <FaExclamationCircle size={12} />
                                                        {t('admin.shipping_pending') || '未発送'}
                                                    </div>
                                                )}
                                                {r.tracking_number && (
                                                    <div className="text-[10px] text-gray-400 mt-1 pl-5">
                                                        {r.tracking_number}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <button
                                                    onClick={() => openTrackingModal(r)}
                                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition shadow-sm ${isShipped
                                                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border border-emerald-300'
                                                        : 'bg-coral text-white hover:opacity-90'
                                                        }`}
                                                >
                                                    {isShipped ? t('admin.shipped') || '発送済' : t('admin.shipping_input_tracking') || '配送入力'}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Yamato CSV Modal */}
            {showCsvModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl animate-in fade-in zoom-in duration-200">
                        <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                            <FaFileCsv className="text-purple-600" />
                            {t('admin.csv_export_title') || 'ヤマトB2CSV出力'}
                        </h3>

                        <div className="space-y-5">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">{t('admin.csv_cool_type') || '配送タイプ'}</label>
                                <div className="grid grid-cols-1 gap-2">
                                    {[
                                        { val: 0, label: t('admin.csv_normal') || '普通配送 (発払い)' },
                                        { val: 1, label: t('admin.csv_cool') || 'クール便 (冷蔵)' },
                                        { val: 2, label: t('admin.csv_frozen') || 'クール便 (冷凍)' },
                                    ].map((it) => (
                                        <label key={it.val} className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition ${csvCoolType === it.val ? 'bg-purple-50 border-purple-300 text-purple-700 ring-2 ring-purple-200' : 'hover:bg-gray-50'}`}>
                                            <input type="radio" name="coolType" value={it.val} checked={csvCoolType === it.val} onChange={() => setCsvCoolType(it.val)} className="w-4 h-4 text-purple-600" />
                                            <span className="text-sm font-medium">{it.label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">{t('admin.csv_ship_date') || '出荷予定日'}</label>
                                <input
                                    type="date"
                                    value={csvShipDate}
                                    onChange={(e) => setCsvShipDate(e.target.value)}
                                    className="w-full border rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-purple-300"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">{t('admin.csv_sender_select') || '発送元住所'}</label>
                                <select
                                    value={selectedSenderId || ''}
                                    onChange={(e) => setSelectedSenderId(Number(e.target.value) || undefined)}
                                    className="w-full border rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-purple-300 appearance-none bg-white"
                                >
                                    <option value="">{t('admin.csv_sender_default') || '未選択（デフォルト）'}</option>
                                    {senderAddresses.map((sa) => (
                                        <option key={sa.id} value={sa.id}>
                                            {sa.name} ({sa.address})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-8">
                            <button
                                onClick={() => setShowCsvModal(false)}
                                className="flex-1 px-4 py-3 border rounded-xl text-gray-600 font-bold hover:bg-gray-50 transition"
                            >
                                {t('common.cancel') || 'キャンセル'}
                            </button>
                            <button
                                onClick={handleGenerateCsv}
                                className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 transition shadow-lg shadow-purple-200"
                            >
                                {t('admin.download') || 'ダウンロード'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tracking Modal */}
            {showTrackingModal && trackingTarget && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl animate-in fade-in zoom-in duration-200">
                        <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                            <FaTruck className="text-coral" />
                            {t('admin.tracking_input_title') || '追跡番号の登録'}
                        </h3>
                        <div className="bg-coral/5 p-3 rounded-xl mb-6">
                            <div className="text-sm font-bold text-navy">{trackingTarget.user_name} 様</div>
                            <div className="text-xs text-coral italic">ID: {trackingTarget.id}</div>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">{t('admin.shipping_tracking_number') || '追跡番号'}</label>
                                <input
                                    type="text"
                                    value={trackingInput}
                                    onChange={(e) => setTrackingInput(e.target.value)}
                                    placeholder={t('admin.tracking_input_placeholder') || '送り状番号 (半角数字)'}
                                    className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-300 text-lg font-mono tracking-wider"
                                    autoFocus
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1 ml-1">{t('admin.shipping_carrier') || '配送業者'}</label>
                                <select
                                    value={trackingCarrier}
                                    onChange={(e) => setTrackingCarrier(e.target.value)}
                                    className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                                >
                                    <option value="yamato">{t('admin.carrier_yamato') || 'ヤマト運輸'}</option>
                                    <option value="sagawa">{t('admin.carrier_sagawa') || '佐川急便'}</option>
                                    <option value="yupack">{t('admin.carrier_yu_pack') || '日本郵便'}</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-8">
                            <button
                                onClick={() => setShowTrackingModal(false)}
                                className="flex-1 px-4 py-3 border rounded-xl text-gray-600 font-bold hover:bg-gray-50 transition"
                            >
                                {t('common.cancel') || 'キャンセル'}
                            </button>
                            <button
                                onClick={saveTrackingNumber}
                                className="flex-1 px-4 py-3 bg-gradient-primary text-white rounded-full shadow-card transition-transform hover:-translate-y-0.5 whitespace-nowrap font-bold hover:opacity-90 transition shadow-lg shadow-blue-200"
                            >
                                {t('admin.tracking_save') || '登録する'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminDeliveryManagement;
